/**
 * Cache-busting reloads that do not throw away the query string.
 *
 * Three places reload the page to recover from a stale deploy (index.html's asset-error
 * handler, main.jsx's chunk-error handler, useVersionCheck). All three used to rebuild the
 * URL from the path alone — `href.split('?')[0] + '?v=' + Date.now()` — which silently
 * dropped every parameter the visitor arrived with.
 *
 * That was not cosmetic. A creator's `?ref=CODE` is read once, on arrival, and a recovery
 * reload landed the visitor on a bare URL with the code gone, so the referral was never
 * attributed and the creator was never paid for the signup. utm tags and invite tokens died
 * the same way, just less visibly.
 *
 * index.html carries its own copy of `versionedUrl` because it runs before any module loads
 * and cannot import this. Keep the two in step.
 */

/** Current URL with a fresh `v` marker, preserving existing params and the hash. */
export function versionedUrl(loc = window.location) {
  const url = new URL(loc.href);
  url.searchParams.set('v', String(Date.now()));
  return url.pathname + url.search + url.hash;
}

/**
 * Has this page already come back from a recovery reload?
 *
 * Checks for a real `v` PARAMETER rather than the substring 'v=', which the callers used to
 * test for. Now that arbitrary params survive the reload, a substring test would match
 * innocent ones — `?inv=1`, `?adv=x`, `?rev=2` all contain 'v=' — and wrongly conclude the
 * reload had already been tried, disabling stale-chunk recovery for those visitors.
 */
export function hasVersionMarker(loc = window.location) {
  return new URLSearchParams(loc.search).has('v');
}
